import { useSuspenseQuery } from '@tanstack/react-query';
import React from 'react';
import { type SubmitHandler, useForm, useFormState } from 'react-hook-form';

import { DEFAULT_SITE_ADAPTIVE_SCHEMA } from '@gitbook/app-adaptive-content';
import { Icon } from '@gitbook/spine-icons';
import {
    ButtonSkeleton,
    Dialog,
    DialogBody,
    DialogCloseButton,
    DialogFooter,
    Form,
    Submit,
    Tab,
    Tabs,
    toast,
    useDialogAction,
} from '@gitbook/spine-react';

import { apiClient, apiQuery } from '../../state';
import { AdaptiveContentFeedbackButton } from './AdaptiveContentFeedbackButton';
import {
    AdaptiveContentExpressionBuilder,
    AdaptiveContentExpressionBuilderReadOnly,
    AdaptiveContentExpressionBuilderReadOnlySkeleton,
    AdaptiveContentExpressionBuilderSkeleton,
} from './ExpressionBuilder';
import {
    AdaptiveContentSiteSchemaEditor,
    AdaptiveContentSiteSchemaEditorSkeleton,
} from './SchemaEditor';
import { AdaptiveContentBuilderStateContext } from './state';
import type {
    AdaptiveContentBuilderDialogSharedProps,
    AdaptiveContentBuilderExpressionTabContext,
    AdaptiveContentBuilderFormValues,
    AdaptiveContentBuilderSchemaTabContext,
    AdaptiveContentBuilderStateContextProps,
    AdaptiveContentBuilderTab,
} from './types';

interface AdaptiveContentBuilderDialogProps extends AdaptiveContentBuilderDialogSharedProps {
    initialTab: AdaptiveContentBuilderTab;
    tabs:
        | [AdaptiveContentBuilderExpressionTabContext]
        | [AdaptiveContentBuilderSchemaTabContext]
        | [AdaptiveContentBuilderExpressionTabContext, AdaptiveContentBuilderSchemaTabContext];
}

/**
 * A dialog for editing adaptive content schema and expressions.
 *
 * It includes 2 tabs:
 * - an expression editor tab for building adaptive content expressions.
 * - a visitor schema editor tab for managing the site’s adaptive schema.
 */
export function AdaptiveContentBuilderDialog(props: AdaptiveContentBuilderDialogProps) {
    const { initialTab, tabs } = props;

    const [selectedTab, setSelectedTab] = React.useState<AdaptiveContentBuilderTab>(initialTab);

    if (tabs.length === 1 && isExpressionTabContext(tabs[0]) && tabs[0].readOnly) {
        const expressionTabContext = tabs[0];
        return (
            <Dialog size="auto">
                <React.Suspense fallback={<AdaptiveContentBuilderDialogReadOnlySkeleton />}>
                    <DialogBody className="flex [&&]:px-10 [&&]:pt-10 [&&]:pb-6">
                        <AdaptiveContentExpressionBuilderReadOnly
                            target={expressionTabContext.target}
                            expression={expressionTabContext.expression ?? ''}
                        />
                    </DialogBody>
                    <DialogFooter>
                        <AdaptiveContentFeedbackButton />
                        <DialogCloseButton label="Close" />
                    </DialogFooter>
                </React.Suspense>
            </Dialog>
        );
    }

    return (
        <Dialog size={{ width: 1080 }}>
            <AdaptiveContentBuilderDialogContent
                {...props}
                selectedTab={selectedTab}
                setSelectedTab={setSelectedTab}
            />
        </Dialog>
    );
}

function AdaptiveContentBuilderDialogContent(
    props: AdaptiveContentBuilderDialogProps & {
        selectedTab: AdaptiveContentBuilderTab;
        setSelectedTab: React.Dispatch<React.SetStateAction<AdaptiveContentBuilderTab>>;
    }
) {
    const { organizationId, siteId, tabs, selectedTab, setSelectedTab } = props;

    const [, startDialogAction] = useDialogAction();

    const expressionTabContext = getExpressionTabContext(tabs);
    const schemaTabContext = getSchemaTabContext(tabs);
    const onSaveExpression = expressionTabContext
        ? expressionTabContext.onSaveExpression
        : undefined;

    const { data: siteSchema } = useSuspenseQuery(
        apiQuery('getSiteAdaptiveSchema', { organizationId, siteId }, { nullOnError: true })
    );
    const siteJSONSchema = JSON.stringify(
        siteSchema?.jsonSchema ?? DEFAULT_SITE_ADAPTIVE_SCHEMA,
        null,
        2
    );

    const form = useForm<AdaptiveContentBuilderFormValues>({
        values: {
            expression: expressionTabContext?.expression,
            jsonSchema: siteJSONSchema,
        },
    });
    const { dirtyFields, errors } = useFormState({ control: form.control });
    const isDirty = Object.values(dirtyFields).some((v) => v);
    const hasErrors = Object.values(errors).some((e) => e);

    const handleFormSubmit: SubmitHandler<AdaptiveContentBuilderFormValues> = async (data) => {
        await startDialogAction(async ({ close }) => {
            await toast.catch(async () => {
                await Promise.all([
                    dirtyFields.jsonSchema && data.jsonSchema.trim().length > 0
                        ? (async () => {
                              await apiClient.sites.updateSiteAdaptiveSchema(
                                  organizationId,
                                  siteId,
                                  { jsonSchema: JSON.parse(data.jsonSchema) }
                              );
                              toast.success('Visitor schema updated');
                          })()
                        : Promise.resolve,
                    'expression' in data &&
                    'expression' in dirtyFields &&
                    typeof data.expression === 'string' &&
                    onSaveExpression
                        ? (async () => {
                              await onSaveExpression(data.expression);
                              toast.success('Adaptive content condition updated');
                          })()
                        : Promise.resolve,
                ]);
                await close();
            }, 'Error while updating visitor schema or expresion');
        });
    };

    const stateContext: AdaptiveContentBuilderStateContextProps = {
        organizationId,
        siteId,
        selectedTab,
        setSelectedTab,
    };

    return (
        <>
            <AdaptiveContentBuilderHeader
                tabs={tabs}
                selectedTab={selectedTab}
                setSelectedTab={setSelectedTab}
            />
            <Form form={form} onSubmit={form.handleSubmit(handleFormSubmit)}>
                <React.Suspense
                    fallback={<AdaptiveContentBuilderDialogSkeleton selectedTab={selectedTab} />}
                >
                    <DialogBody
                        className="flex h-[70vh] overflow-x-hidden [&&]:p-0"
                        scrollable={false}
                    >
                        <AdaptiveContentBuilderStateContext.Provider value={stateContext}>
                            {selectedTab === 'expression' && expressionTabContext ? (
                                <AdaptiveContentExpressionBuilder
                                    target={expressionTabContext.target}
                                />
                            ) : null}
                            {selectedTab === 'schema' && schemaTabContext ? (
                                <AdaptiveContentSiteSchemaEditor />
                            ) : null}
                        </AdaptiveContentBuilderStateContext.Provider>
                    </DialogBody>
                    <DialogFooter>
                        <AdaptiveContentFeedbackButton />
                        <DialogCloseButton onClick={() => form.reset()} />
                        <Submit
                            label={expressionTabContext ? 'Save condition & schema' : 'Save schema'}
                            disabled={!isDirty || hasErrors}
                        />
                    </DialogFooter>
                </React.Suspense>
            </Form>
        </>
    );
}

function getExpressionTabContext(
    tabs: AdaptiveContentBuilderDialogProps['tabs']
): AdaptiveContentBuilderExpressionTabContext | undefined {
    const found = tabs.find(isExpressionTabContext);

    return found && isExpressionTabContext(found) ? found : undefined;
}

function getSchemaTabContext(
    tabs: AdaptiveContentBuilderDialogProps['tabs']
): AdaptiveContentBuilderSchemaTabContext | undefined {
    const found = tabs.find(isSchemaTabContext);

    return found && isSchemaTabContext(found) ? found : undefined;
}

function isExpressionTabContext(
    context: AdaptiveContentBuilderExpressionTabContext | AdaptiveContentBuilderSchemaTabContext
): context is AdaptiveContentBuilderExpressionTabContext {
    return context.tab === 'expression';
}

function isSchemaTabContext(
    context: AdaptiveContentBuilderExpressionTabContext | AdaptiveContentBuilderSchemaTabContext
): context is AdaptiveContentBuilderSchemaTabContext {
    return context.tab === 'schema';
}

function AdaptiveContentBuilderHeader(props: {
    selectedTab: AdaptiveContentBuilderTab;
    setSelectedTab: React.Dispatch<React.SetStateAction<AdaptiveContentBuilderTab>>;
    tabs: AdaptiveContentBuilderDialogProps['tabs'];
}) {
    const { selectedTab, setSelectedTab, tabs } = props;
    const expressionTabContext = getExpressionTabContext(tabs);
    const schemaTabContext = getSchemaTabContext(tabs);

    return tabs.length > 0 ? (
        <Tabs className="flex border-b px-10 pt-4">
            {expressionTabContext && (
                <Tab
                    active={selectedTab === 'expression'}
                    label="Access condition"
                    leadingIcon={Icon.Condition}
                    onPress={() => setSelectedTab('expression')}
                />
            )}
            {schemaTabContext && (
                <Tab
                    active={selectedTab === 'schema'}
                    label="Visitor schema"
                    leadingIcon={Icon.Schema}
                    onPress={() => setSelectedTab('schema')}
                />
            )}
        </Tabs>
    ) : null;
}

function AdaptiveContentBuilderDialogSkeleton(props: { selectedTab: AdaptiveContentBuilderTab }) {
    const { selectedTab } = props;
    return (
        <>
            <DialogBody className="h-[70vh] [&&]:p-0" scrollable={false}>
                {selectedTab === 'expression' && <AdaptiveContentExpressionBuilderSkeleton />}
                {selectedTab === 'schema' && <AdaptiveContentSiteSchemaEditorSkeleton />}
            </DialogBody>
            <DialogFooter>
                <ButtonSkeleton label="Cancel" />
                <ButtonSkeleton label="Confirm" />
            </DialogFooter>
        </>
    );
}

function AdaptiveContentBuilderDialogReadOnlySkeleton() {
    return (
        <>
            <DialogBody className="h-[70vh] [&&]:px-10 [&&]:pt-10 [&&]:pb-6">
                <AdaptiveContentExpressionBuilderReadOnlySkeleton />
            </DialogBody>
            <DialogFooter>
                <DialogCloseButton label="Close" />
            </DialogFooter>
        </>
    );
}
